import { useMutation } from 'react-query';
import usePlanStore from 'store/planStore';
import { getEstimatedBudget } from 'api/services/mediaPlanning/calculateBudget';
import { Countries } from 'shared/enums';
import {
  MetaConfigBody,
  TikTokConfigBody,
  YouTubeConfigBody,
} from 'api/services/mediaPlanning/types';
import { normalizeTouchpointName } from 'shared/utils';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useError } from '../../../common/Errors/ErrorContext/ErrorContext';
import useMediaPlanMetaSync from '../../../hooks/useMediaPlanMetaSync';

export interface Touchpoints {
  youtube?: YouTubeConfigBody;
  meta?: MetaConfigBody;
  tiktok?: TikTokConfigBody;
  [key: string]: object | undefined;
}

const useCalculateBudget = (
  getMetaPredictions?: () => void,
  getMetaPredictionStatuses?: () => void
) => {
  const {
    generalInfoState,
    alreadySavedPlanId,
    countryForms,
    selectedCountry,
    selectedCountryISOCode,
    updateCalculatedBudget,
    updateMetaConfig,
    updateMetaPredictionsState,
  } = usePlanStore();

  const countryForm = countryForms[selectedCountry!];
  const { showError } = useError();
  const { id } = useParams();
  const { syncMediaPlan } = useMediaPlanMetaSync();

  const {
    mutate: calculateBudget,
    mutateAsync: asyncCalculateBudget,
    data: response,
    isLoading,
    isError,
  } = useMutation(
    async ({ availableTouchpoints }: { availableTouchpoints: string[] }) => {
      const { youTubeConfig } = countryForms[selectedCountry!];
      const { metaConfig } = countryForms[selectedCountry!];
      const { tiktokConfig } = countryForms[selectedCountry!];

      const automaticTouchpoints = availableTouchpoints
        .filter(
          (touchpoint) =>
            !['youtube', 'meta', 'tiktok'].includes(
              normalizeTouchpointName(touchpoint)
            )
        )
        .map((touchpoint) => ({
          [normalizeTouchpointName(touchpoint)]: {},
        }));

      const touchpoints: Touchpoints = Object.assign(
        {
          youtube: undefined,
          meta: undefined,
          tiktok: undefined,
        },
        ...automaticTouchpoints
      );

      if (youTubeConfig) {
        touchpoints.youtube = {
          touchpointTargetGroup:
            typeof youTubeConfig?.targetGroup === 'object'
              ? youTubeConfig?.targetGroup?.targetGroup
              : youTubeConfig.targetGroup,
          saturation: youTubeConfig?.saturation,
          partners: youTubeConfig?.partners?.map((partner) => partner.id) ?? [],
          ...(process.env.REACT_APP_API_VERSION !== 'v3' && {
            skipShare: youTubeConfig?.skipValue,
            unskipShare: youTubeConfig?.unSkipValue,
          }),
          bumperShare: youTubeConfig?.bumperValue,
          budgetShare: youTubeConfig?.partnerBudgetShare,
        };
      }

      if (metaConfig) {
        touchpoints.meta = {
          touchpointTargetGroup:
            typeof metaConfig?.targetGroup === 'object' &&
            metaConfig?.targetGroup !== null
              ? (metaConfig.targetGroup as any).targetGroup
              : metaConfig?.targetGroup,
          saturation: metaConfig?.saturation ?? null,
          startDate: moment(metaConfig?.startDate, 'DD-MM-YYYY').format(
            'YYYY-MM-DD'
          ),
          campaignLength: metaConfig?.campaignLength,
          partners: metaConfig?.partners?.map((partner) => partner.id) ?? [],
          partnerBudgetShareConsideration:
            metaConfig?.partnerBudgetShareConsideration,
          partnerBudgetShareConversion:
            metaConfig?.partnerBudgetShareConversion,
          isConvertsToSales: metaConfig?.metaConverts,
          reachEstimateMin: metaConfig?.reachEstimateMin,
          campaignDestination: metaConfig?.campaignDestination,
          metaCampaignId: metaConfig.metaCampaignId,
          metaPredictionId: metaConfig.metaPredictionId,
          metaAccountId: metaConfig.metaAccountId,
        };
      }

      if (tiktokConfig) {
        touchpoints.tiktok = {
          touchpointTargetGroup:
            typeof tiktokConfig?.targetGroup === 'object'
              ? tiktokConfig.targetGroup?.targetGroup
              : tiktokConfig.targetGroup,
          saturation: tiktokConfig?.saturation,
          partners: tiktokConfig?.partners?.map((partner) => partner.id) ?? [],
          partnerBudgetShare: tiktokConfig?.partnerBudgetShare,
        };
      }

      return getEstimatedBudget({
        body: {
          campaignName: generalInfoState.campaignName,
          division: generalInfoState.division,
          brand: generalInfoState.brand,
          category: generalInfoState.category,
          countryISO: selectedCountryISOCode,
          mediaPlanId: id || alreadySavedPlanId || null,
          campaignTargetGroup: countryForm.targetGroup,
          campaignScenario: countryForm.campaignScenario,
          segments: countryForm.segments.map((segment) => segment.id),
          startDate: moment(countryForm.startDate, 'DD-MM-YYYY').format(
            'YYYY-MM-DD'
          ),
          campaignLength: Number(countryForm.campaignLength),
          touchpoints,
          isTVIncluded: countryForm.isTVIncluded,
          isTVMandatory: countryForm.isTVMandatory,
        },
        countryISO: selectedCountryISOCode as Countries,
      });
    },
    {
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message || error.message || 'Unknown error';
        showError(errorMessage);

        const isAnyMetaNotReserved = Object.values(countryForms).some(
          (country) => country.metaConfig?.reservationStatus !== 'reserved'
        );

        if (getMetaPredictionStatuses && isAnyMetaNotReserved) {
          getMetaPredictionStatuses();
        }
      },
      onSuccess: async (successResponse) => {
        if (selectedCountry && successResponse?.data) {
          const calculatedBudget = successResponse.data;
          const guardedCalculateBudget = calculatedBudget.map((budgetRow) => {
            if (budgetRow.metaPredictionId && budgetRow.metaCampaignId) {
              updateMetaConfig(selectedCountry!, {
                metaPredictionStatuses: budgetRow.metaPredictionStatuses,
                isMetaPartialPredictionEnabled:
                  budgetRow.isMetaPartialPredictionEnabled,
              });

              updateMetaPredictionsState(
                selectedCountry,
                budgetRow.metaCampaignId,
                budgetRow.metaPredictionId
              );
            }

            return {
              ...budgetRow,
              impressions: budgetRow.impressions ?? 0,
              videoViews: budgetRow.videoViews ?? 0,
              clicks: budgetRow.clicks ?? 0,
              reach: budgetRow.reach ?? 0,
            };
          });

          updateCalculatedBudget(
            selectedCountry,
            guardedCalculateBudget.map((row) => ({
              ...row,
              isIncludedInCalculations: !!row.isMandatory,
            }))
          );

          updateCalculatedBudget(
            selectedCountry,
            guardedCalculateBudget.map((row) => ({
              ...row,
              isIncludedInCalculations: !!row.isMandatory,
            }))
          );

          if (id || alreadySavedPlanId) {
            if (countryForms[selectedCountry].savedCalculatedBudget?.length) {
              syncMediaPlan(id || alreadySavedPlanId);
            }

            const isAnyMetaNotReserved = Object.values(countryForms).some(
              (country) => country.metaConfig?.reservationStatus !== 'reserved'
            );

            if (getMetaPredictionStatuses && isAnyMetaNotReserved) {
              getMetaPredictionStatuses();
            }
          }
        }
      },
    }
  );

  return {
    calculateBudget,
    asyncCalculateBudget,
    data: response?.data,
    isLoading,
    isError,
  };
};

export default useCalculateBudget;
